import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { useDetectorKeywordsReportQuery } from '@/lib/query-options';
import { CampaignAverages } from './CampaignAverages';
import { AdsAndAdGroups } from './AdsAndAdGroups';
import { Keywords } from './Keywords';
import { KeywordsList } from './KeywordsList';
import { Ad } from '@/lib/types';

// Search Terms (rows in search terms table)
interface KeyWord {
  keyword: string;
  googleads_ad_group_id: string;
  impressions: number;
  clicks: number;
  conversions: number;
  cost: number;
  ctr: number;
  cvr: number;
  matched_rules: Array<{
    confidence_level: string;
    priority: number | string;
    tags: string[];
    reason: string;
    campaign_type: string;
  }>;
  rule_results?: Record<string, { reason: string }>;
}

// Type guard to check if a term is a SearchTerm object
function isSearchTerm(term: unknown): term is KeyWord {
  return !!term && typeof term === 'object' && 'keyword' in term;
}

/* KeywordsDetectorDashboard Component */
export const KeywordsDetectorDashboard = ({
  campaignId,
  detector,
  detectorLabel,
  threshold,
}: {
  campaignId?: number;
  detector?: string;
  detectorLabel?: string;
  threshold?: number;
}) => {
  // Use React Query for data fetching with caching
  const { data: reportData, isLoading, error: queryError } = useDetectorKeywordsReportQuery(campaignId || 0, detector || '', threshold);
  const error = queryError?.message || null;
  ``;
  const keywords = reportData?.keywords || [];
  const [selectedAdId, setSelectedAdId] = useState<number | null>(null);
  const [selectedAdGroupId, setSelectedAdGroupId] = useState<string | null>(null);
  // Extract values from API response
  const campaignAverages = reportData?.campaign_averages || null;
  const numericCampaignId = typeof reportData?.id === 'number' ? reportData.id : null;
  const campaignAds = reportData?.ads || [];
  const [selectedTerms, setSelectedTerms] = useState<Set<string>>(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

  // Helper functions for managing selections
  const toggleTermSelection = (term: KeyWord | string) => {
    // Handle string terms (simple list)
    if (typeof term === 'string') {
      const newSelected = new Set(selectedTerms);
      newSelected.has(term) ? newSelected.delete(term) : newSelected.add(term);
      setSelectedTerms(newSelected);
      setSelectAllChecked(false);
      return;
    }

    const termKey = `${term.keyword}-${term.googleads_ad_group_id}`;
    const newSelected = new Set(selectedTerms);
    newSelected.has(termKey) ? newSelected.delete(termKey) : newSelected.add(termKey);
    setSelectedTerms(newSelected);
    setSelectAllChecked(false);
  };

  // Function to toggle all terms selection
  const toggleAllTerms = (checked: boolean) => {
    const filteredTerms = applyFilters(keywords);
    const newSelected = new Set(selectedTerms);

    if (checked) {
      // Add all filtered terms to selection
      filteredTerms.forEach((term) => {
        if (typeof term === 'string') {
          newSelected.add(term);
        }
      });
    } else {
      // Remove all filtered terms from selection
      filteredTerms.forEach((term) => {
        if (typeof term === 'string') {
          newSelected.delete(term);
        } else {
          const termKey = `${term.keyword}-${term.googleads_ad_group_id}`;
          newSelected.delete(termKey);
        }
      });
    }

    setSelectedTerms(newSelected);
    setSelectAllChecked(checked);
  };

  // Apply filters for ad and ad group
  const applyFilters = (terms: (KeyWord | string)[]) => {
    if (!terms || !Array.isArray(terms)) {
      return [];
    }

    // If terms are strings, return empty array since SearchTerms expects SearchTerm[]
    if (terms.length > 0 && typeof terms[0] === 'string') {
      return [];
    }

    // Filter only SearchTerm objects
    return terms.filter((term): term is KeyWord => {
      if (!isSearchTerm(term)) return false;

      // If no filters are active, return all terms
      if (selectedAdId === null && selectedAdGroupId === null) {
        return true;
      }

      // If ad ID filter is active
      if (selectedAdId !== null && term.googleads_ad_group_id) {
        // Find the ad in campaignAds
        const ad = (campaignAds as Ad[]).find((ad: Ad) => ad.id === selectedAdId);
        // If the ad exists and has an ad group ID, filter by it
        if (ad && 'googleads_ad_group_id' in ad) {
          // Compare as strings to ensure consistent comparison
          const termAdGroupId = String(term.googleads_ad_group_id);
          const adAdGroupId = String((ad as Ad & { googleads_ad_group_id?: string }).googleads_ad_group_id);

          // If the term's ad group ID matches the ad's ad group ID, include it
          if (termAdGroupId === adAdGroupId) {
            return true;
          }
          return false;
        }
      }

      // If ad group ID filter is active
      if (selectedAdGroupId !== null && term.googleads_ad_group_id) {
        // Compare as strings
        return String(term.googleads_ad_group_id) === String(selectedAdGroupId);
      }

      return true;
    });
  };

  // Wrapper function for onAdClick to ensure it only receives numbers
  const handleAdClick = (adId: number) => {
    setSelectedAdId(adId);
  };

  // Initialize data when reportData changes
  useEffect(() => {
    if (reportData?.search_terms) {

      // Reset selected filters when data changes
      setSelectedAdId(null);
      setSelectedAdGroupId(null);
    }
  }, [reportData]);

  // Check if searchTerms is a simple string array
  const isSimpleStringArray = keywords.length > 0 && typeof keywords[0] === 'string';

  return (
    <Card className='p-6 bg-white border'>
      <div className='space-y-6'>
        <div className='flex items-center justify-between mb-6'>
          <h3 className='text-lg font-medium'>{detectorLabel}</h3>
        </div>

        {/* Campaign Averages Section */}
        <CampaignAverages
          campaignAverages={campaignAverages}
          searchTermsLength={keywords.length}
          isLoading={isLoading}
        />

        {/* Ads and Ad Groups Section */}
        <AdsAndAdGroups
          campaignAds={campaignAds}
          isLoading={isLoading}
          campaignId={campaignId}
          numericCampaignId={numericCampaignId}
          onAdClick={handleAdClick}
        />

        {/* Search Terms Section - Conditionally render based on data type */}
        {isSimpleStringArray ? (
          <KeywordsList keywords={keywords as string[]} isLoading={isLoading} error={error} />
        ) : (
          <Keywords
            keywords={keywords}
            isLoading={isLoading}
            error={error}
            selectedTerms={selectedTerms}
            selectAllChecked={selectAllChecked}
            onTermSelect={toggleTermSelection}
            onSelectAll={toggleAllTerms}
            campaignAds={campaignAds}
          />
        )}
      </div>
    </Card>
  );
};
